// client/src/components/leagues/LeagueStandings.js
import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import StandingsTable from './StandingsTable'
import useLeagueStandings from '../../hooks/useLeagueStandings'
import { COMPLETED_STATUSES } from '../../utils/matchStatus'

export default function LeagueStandings() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState('overall') // 'overall' or 'divisions'

  const {
    loading,
    error,
    leagueData,
    overallStandings,
    divisionStandings,
    currentPlayerRank,
    selectedLeagueLevelId,
    setSelectedLeagueLevelId,
    refetch,
  } = useLeagueStandings(leagueId)
  const activeLevels = leagueData?.active_levels || []
  const hasLevelTabs = activeLevels.length > 1

  const matchFormat = leagueData?.matchFormat || 'TRADITIONAL'
  const minimumMatchesForEligibility =
    leagueData?.playoffMinMatchesRequired || 3

  if (loading) {
    return (
      <PageLayout title="League Standings">
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading league standings...</p>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout title="League Standings">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Standings</h4>
          <p>{error}</p>
          <hr />
          <button className="btn btn-primary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </PageLayout>
    )
  }

  // If we get here without leagueData, something unexpected happened
  if (!leagueData) {
    return (
      <PageLayout title="League Standings">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Standings</h4>
          <p>Unable to load league standings.</p>
          <hr />
          <button className="btn btn-primary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="League Standings">
      <div className="container-fluid">
        {/* Header with view toggle */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>
                  {viewMode === 'overall'
                    ? 'League Standings'
                    : 'Division Standings'}
                </h2>
                <p className="text-muted mb-1">League #{leagueId}</p>
                {leagueData?.champion?.name && (
                  <p className="text-muted mb-1">
                    Champion: {leagueData.champion.name}
                  </p>
                )}
                {currentPlayerRank > 0 && (
                  <p className="text-success mb-0 small">
                    🏆 You are ranked #{currentPlayerRank} in your division
                  </p>
                )}
              </div>

              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${
                    viewMode === 'overall'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setViewMode('overall')}
                >
                  Overall Rankings
                </button>
                <button
                  type="button"
                  className={`btn ${
                    viewMode === 'divisions'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setViewMode('divisions')}
                >
                  By Division
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info d-none d-md-inline-block"
                  onClick={() => navigate(`/leagues/${leagueId}/matches`)}
                >
                  🎾 Division History
                </button>
              </div>
            </div>
          </div>
        </div>

        {hasLevelTabs && (
          <div className="row mb-3">
            <div className="col-12">
              <ul className="nav nav-tabs">
                {activeLevels.map((level) => (
                  <li className="nav-item" key={level.id}>
                    <button
                      type="button"
                      className={`nav-link ${
                        selectedLeagueLevelId === level.id ? 'active' : ''
                      }`}
                      data-testid={`league-level-tab-${level.id}`}
                      onClick={() => setSelectedLeagueLevelId(level.id)}
                    >
                      {level.skill_level}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Overall standings view */}
        {viewMode === 'overall' && (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <StandingsTable
                    standings={overallStandings}
                    title="League Standings"
                    showDivision={true}
                    showSets={true}
                    showGames={true}
                    compact={false}
                    leagueId={Number(leagueId)}
                    matchFormat={matchFormat}
                    minimumMatchesForEligibility={minimumMatchesForEligibility}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Divisions view */}
        {viewMode === 'divisions' && (
          <div className="row">
            {Object.entries(divisionStandings).map(
              ([divisionKey, divisionData]) => {
                const { divisionId, divisionName, players } = divisionData
                return (
                  <div key={divisionKey} className="col-lg-6 mb-4">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <StandingsTable
                          standings={players}
                          title={divisionName}
                          showDivision={false}
                          showSets={true}
                          showGames={true}
                          compact={true}
                          leagueId={Number(leagueId)}
                          divisionId={divisionId}
                          matchFormat={matchFormat}
                          minimumMatchesForEligibility={
                            minimumMatchesForEligibility
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              },
            )}
          </div>
        )}

        {/* Statistics summary */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-light">
              <div className="card-body">
                <h5 className="mb-3">📊 League Statistics</h5>
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="h4 text-primary">
                      {leagueData.allPlayers.length}
                    </div>
                    <div className="small text-muted">Total Players</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h4 text-success">
                      {
                        leagueData.allMatches.filter((m) =>
                          COMPLETED_STATUSES.has(m.status),
                        ).length
                      }
                    </div>
                    <div className="small text-muted">Completed Matches</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h4 text-warning">
                      {
                        leagueData.allMatches.filter(
                          (m) => m.status === 'scheduled',
                        ).length
                      }
                    </div>
                    <div className="small text-muted">Scheduled Matches</div>
                  </div>
                  <div className="col-md-3">
                    <div className="h4 text-info">
                      {Object.keys(divisionStandings).length}
                    </div>
                    <div className="small text-muted">Divisions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
